import PropTypes from "prop-types"
import React from "react"

import Badge from "src/components/Badge"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import DataTableV2 from "src/components/DataTableV2"
import FormIconTextField from "src/components/Form/FormIconTextField"

import { getAuditLog, printAuditLog } from "src/api/DryStack"

import useDatatable from "src/hooks/use_datatable"

import { statusBadgeColor } from "src/utils/status_badge_color_helper"
import { titlecase } from "src/utils/string_helpers"

import LaunchReportEmptyState from "./LaunchReportEmptyState"

const renderStatusBadge = (status) => {
  const displayStatus = titlecase(status)
  return <Badge text={displayStatus} color={statusBadgeColor(displayStatus)} />
}

const AuditLog = ({ marinaSlug }) => {
  const table = useDatatable({
    queryKey: ["auditLog", marinaSlug],
    fetchFn: getAuditLog,
    defaultFilters: { search: "" },
    infinite: true,
    syncFiltersToUrl: false,
  })

  const columns = [
    {
      key: "changedAtFormatted",
      header: "Timestamp",
      width: "180px",
    },
    {
      key: "actorType",
      header: "Performed By",
      width: "160px",
      render: (row) => (
        <div className="flex flex-col">
          <span className="truncate">{row.actorType}</span>
          <span className="truncate text-xs text-gray-600">
            {row.actorName || row.actorEmail}
          </span>
        </div>
      ),
    },
    {
      key: "fromStatus",
      header: "From",
      width: "100px",
      render: (row) => renderStatusBadge(row.fromStatus),
    },
    {
      key: "toStatus",
      header: "To",
      width: "100px",
      render: (row) => renderStatusBadge(row.toStatus),
    },
    {
      key: "contactBoatName",
      header: "Boat Name",
      width: "160px",
      render: (row) => {
        if (!row.contactBoat) return row.contactBoatName

        return <BoatPreviewLink boat={row.contactBoat} />
      },
    },
    {
      key: "contactName",
      header: "Boater",
      width: "140px",
      render: (row) => (
        <a
          href={`/manage/${marinaSlug}/contacts/${row.contactEncodedId}`}
          className="text-blue-600 hover:underline"
        >
          {row.contactName}
        </a>
      ),
    },
  ]

  const handleSearchChange = (e) => {
    table.setFilter("search", e.target.value)
  }

  const handleClearSearch = () => {
    table.clearFilter("search")
  }

  const handleScroll = (e) => {
    if (!table.hasNextPage || table.isFetchingNextPage) return
    const { scrollHeight, scrollTop, clientHeight } = e.target
    if (scrollHeight - scrollTop - 200 <= clientHeight) {
      table.fetchNextPage()
    }
  }

  const hasNoResults =
    !table.isLoading && !table.isFetching && table.records.length === 0

  const searchValue = table.filters.search || ""

  if (hasNoResults) {
    return (
      <div className="px-4">
        <div className="mb-4 w-full lg:w-1/2">
          <FormIconTextField
            icon={
              <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
            }
            placeholder="Search by boat, boater, status, or staff name..."
            value={searchValue}
            onChange={handleSearchChange}
            isClearable
            onClearSelection={handleClearSearch}
          />
        </div>
        {searchValue ? (
          <LaunchReportEmptyState
            title="No results found"
            description={`No audit log entries match "${searchValue}". Try a different search term.`}
          />
        ) : (
          <LaunchReportEmptyState
            title="No audit log entries"
            description="Launch status changes will appear here once boaters or staff start scheduling and launching boats."
          />
        )}
      </div>
    )
  }

  return (
    <div className="px-4">
      <div className="mb-4 w-full lg:w-1/2">
        <FormIconTextField
          icon={
            <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
          }
          placeholder="Search by boat, boater, status, or staff name..."
          value={searchValue}
          onChange={handleSearchChange}
          isClearable
          onClearSelection={handleClearSearch}
        />
      </div>
      <DataTableV2
        name="launchAuditLog"
        columns={columns}
        data={table.records}
        hideActions
        hideSettings
        maxHeight="600px"
        rowCount={8}
        onScroll={handleScroll}
        onPrint={(selectedIds) =>
          printAuditLog({ ids: selectedIds }).then((r) => r.data)
        }
        {...table.tableProps}
      />
    </div>
  )
}

AuditLog.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
}

export default AuditLog
